<div class="org">
  <header class="org-header">
    <p class="org-eyebrow" data-testid="org-name">{{ organizationName() || 'Organization' }}</p>
    <h1 class="org-title">Members</h1>
    <p class="org-subtitle">
      The owner and admins invite people by email; they choose their own name and password when they accept.
      Members see the projects of their teams, the owner and admins see every project.
      <a routerLink="/knowledge-base/getting-started/invite-your-team" class="org-link">Roles explained</a>
    </p>
    <nav class="org-tabs" aria-label="Organization">
      <a routerLink="/main/organization" routerLinkActive="is-active" [routerLinkActiveOptions]="{ exact: true }">Members</a>
      <a routerLink="/main/organization/teams" routerLinkActive="is-active" data-testid="org-tab-teams">Teams</a>
    </nav>
  </header>

  @if (loading()) {
    <div class="o-card o-card--skeleton" data-testid="org-loading">
      <div class="sk sk--title"></div>
      <div class="sk"></div>
      <div class="sk sk--short"></div>
    </div>
  } @else if (loadError()) {
    <div class="o-card o-empty" data-testid="org-load-error">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <p>Couldn’t load the members of your organization.</p>
    </div>
  } @else {

    <!-- Access requests -->
    @if (canManage() && accessRequests().length) {
      <section class="o-card o-card--attention" data-testid="org-access-requests">
        <h2 class="o-card__title">
          Access requests <span class="o-count o-count--attention">{{ accessRequests().length }}</span>
        </h2>
        <p class="o-hint">These people opened a link to one of your projects and asked to join. Approving emails them an invitation.</p>
        <ul class="o-list">
          @for (request of accessRequests(); track request.uuid) {
            <li class="o-row" data-testid="org-access-request">
              <span class="o-avatar o-avatar--muted" aria-hidden="true">{{ initials(request) }}</span>
              <div class="o-row__main">
                <span class="o-row__name">{{ request.name }}</span>
                <span class="o-row__meta">
                  {{ request.email }}
                  @if (request.project) { · wanted to open <strong>{{ request.project.name }}</strong> }
                @if (request.project?.teams?.length) {
                  · approving adds them to {{ teamNames(request.project!.teams) }}
                }
                  · {{ request.created_at | date: 'mediumDate' }}
                </span>
                @if (request.message) {
                  <q class="o-row__message">{{ request.message }}</q>
                }
              </div>
              <div class="o-row__actions">
                <button class="pw-btn pw-btn--primary pw-btn--sm" type="button" [disabled]="busy() === request.uuid"
                  (click)="approve(request)" data-testid="org-approve">Approve</button>
                <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" [disabled]="busy() === request.uuid"
                  (click)="dismiss(request)" data-testid="org-dismiss">Dismiss</button>
              </div>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Invite -->
    @if (canManage()) {
    <section class="o-card">
      <h2 class="o-card__title">Invite someone</h2>
      <form class="o-invite" (submit)="$event.preventDefault(); invite()">
        <input class="o-input" type="email" maxlength="255" placeholder="colleague@company.com" aria-label="Email address"
          autocomplete="off" data-testid="org-invite-email"
          [value]="inviteEmail()" (input)="inviteEmail.set($any($event.target).value); inviteError.set(null)">
        <select class="o-select" aria-label="Role" data-testid="org-invite-role"
          [value]="inviteRole()" (change)="inviteRole.set($any($event.target).value)">
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        <button class="pw-btn pw-btn--primary" type="submit" [disabled]="!inviteEmailValid() || inviting()"
          data-testid="org-invite-submit">
          <i class="fa" [class.fa-paper-plane]="!inviting()" [class.fa-spinner]="inviting()" [class.fa-spin]="inviting()"
            aria-hidden="true"></i>
          Send invitation
        </button>
      </form>
      @if (teams().length) {
        <div class="o-team-pick" data-testid="org-invite-teams">
          <span>Teams:</span>
          @for (team of teams(); track team.uuid) {
            <label [class.is-on]="inviteTeams().has(team.uuid)">
              <input type="checkbox" [checked]="inviteTeams().has(team.uuid)" (change)="toggleInviteTeam(team.uuid)">
              {{ team.name }}
            </label>
          }
        </div>
      }
      @if (inviteError()) {
        <p class="o-error" role="alert" data-testid="org-invite-error">{{ inviteError() }}</p>
      }

      @if (lastInvite(); as sent) {
        <div class="o-sent" data-testid="org-invite-sent">
          <p>
            @if (sent.email_sent) {
              <i class="fa fa-check-circle" aria-hidden="true"></i> We emailed <strong>{{ sent.email }}</strong> an invitation.
              You can also send them this link:
            } @else {
              <i class="fa fa-exclamation-circle" aria-hidden="true"></i> The email to <strong>{{ sent.email }}</strong>
              couldn’t be sent. Share this link with them instead:
            }
          </p>
          <div class="o-link">
            <code data-testid="org-invite-link">{{ sent.invite_url }}</code>
            <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="copyLink()">
              <i class="fa" [class.fa-copy]="!copied()" [class.fa-check]="copied()" aria-hidden="true"></i>
              {{ copied() ? 'Copied' : 'Copy link' }}
            </button>
          </div>
          <p class="o-hint">The link works once and expires on {{ sent.expires_at | date: 'mediumDate' }}. It won’t be shown again.</p>
        </div>
      }
    </section>
    } @else {
      <section class="o-card o-note" data-testid="org-member-note">
        <i class="fa fa-info-circle" aria-hidden="true"></i>
        <p>You’re a <strong>member</strong>. Ask the owner or an admin to invite someone or to change roles.</p>
      </section>
    }

    <!-- Pending invitations -->
    @if (canManage() && invitations().length) {
      <section class="o-card" data-testid="org-invitations">
        <h2 class="o-card__title">Pending invitations <span class="o-count">{{ invitations().length }}</span></h2>
        <ul class="o-list">
          @for (invitation of invitations(); track invitation.uuid) {
            <li class="o-row" data-testid="org-invitation">
              <span class="o-avatar o-avatar--muted" aria-hidden="true"><i class="fa fa-envelope-o"></i></span>
              <div class="o-row__main">
                <span class="o-row__name">
                  {{ invitation.email }} <span class="o-role o-role--{{ invitation.role }}">{{ roleLabels[invitation.role] }}</span>
                </span>
                <span class="o-row__meta">
                  Invited{{ invitation.invited_by ? ' by ' + invitation.invited_by : '' }} · expires {{ invitation.expires_at | date: 'mediumDate' }}
                  @if (invitation.teams?.length) { · joins {{ teamNames(invitation.teams) }} }
                </span>
              </div>
              <div class="o-row__actions">
                <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" [disabled]="busy() === invitation.uuid"
                  (click)="resend(invitation)">Resend</button>
                <button class="pw-btn pw-btn--danger-outline pw-btn--sm" type="button" [disabled]="busy() === invitation.uuid"
                  (click)="revoke(invitation)">Revoke</button>
              </div>
            </li>
          }
        </ul>
      </section>
    }

    <!-- Members -->
    <section class="o-card" data-testid="org-members">
      <h2 class="o-card__title">Members <span class="o-count">{{ members().length }}</span></h2>
      <ul class="o-list">
        @for (member of members(); track member.uuid) {
          <li class="o-row" data-testid="org-member">
            <span class="o-avatar" aria-hidden="true">{{ initials(member) }}</span>
            <div class="o-row__main">
              <span class="o-row__name">
                {{ member.first_name }} {{ member.last_name }}
                <span class="o-role o-role--{{ member.role }}" data-testid="org-member-role">{{ roleLabels[member.role] }}</span>
                @if (member.is_you) { <span class="o-badge">You</span> }
              </span>
              <span class="o-row__meta">
                {{ member.email }}
                · {{ member.last_login ? 'last active ' + (member.last_login | date: 'mediumDate') : 'never logged in' }}
              </span>
              @if (member.teams?.length) {
                <span class="o-teams" data-testid="org-member-teams">
                  @for (team of member.teams; track team.uuid) { <span class="o-team">{{ team.name }}</span> }
                </span>
              } @else if (member.role === 'member') {
                <span class="o-row__meta o-row__meta--warn">In no team yet, so they see no projects.</span>
              }
            </div>
            @if (confirmingTransfer() === member.uuid) {
              <div class="o-row__actions">
                <span class="o-row__confirm">Make {{ member.first_name || member.email }} the owner? You become admin.</span>
                <button class="pw-btn pw-btn--primary pw-btn--sm" type="button" [disabled]="busy() === member.uuid"
                  (click)="transferOwnership(member)" data-testid="org-transfer-confirm">Transfer</button>
                <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="confirmingTransfer.set(null)">Cancel</button>
              </div>
            } @else if (isOwner() && !member.is_you) {
              <div class="o-row__actions">
                <select class="o-select o-select--sm" aria-label="Role" data-testid="org-member-role-select"
                  [value]="member.role" [disabled]="busy() === member.uuid"
                  (change)="changeRole(member, $any($event.target).value)">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="confirmingTransfer.set(member.uuid)"
                  data-testid="org-transfer">Make owner</button>
                @if (confirmingRemove() === member.uuid) {
                  <button class="pw-btn pw-btn--danger pw-btn--sm" type="button" [disabled]="busy() === member.uuid"
                    (click)="remove(member)" data-testid="org-remove-confirm">Yes, remove</button>
                  <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="confirmingRemove.set(null)">Cancel</button>
                } @else {
                  <button class="pw-btn pw-btn--danger-outline pw-btn--sm" type="button"
                    (click)="confirmingRemove.set(member.uuid)" data-testid="org-remove">Remove</button>
                }
              </div>
            } @else if (canRemove(member)) {
              <div class="o-row__actions">
                @if (confirmingRemove() === member.uuid) {
                  <span class="o-row__confirm">Remove?</span>
                  <button class="pw-btn pw-btn--danger pw-btn--sm" type="button" [disabled]="busy() === member.uuid"
                    (click)="remove(member)" data-testid="org-remove-confirm">Yes, remove</button>
                  <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="confirmingRemove.set(null)">Cancel</button>
                } @else {
                  <button class="pw-btn pw-btn--danger-outline pw-btn--sm" type="button"
                    (click)="confirmingRemove.set(member.uuid)" data-testid="org-remove">Remove</button>
                }
              </div>
            }
          </li>
        }
      </ul>
      <p class="o-hint o-hint--foot">
        Removing someone signs them out and disables their API keys right away. Their projects and history stay.
      </p>
    </section>
  }
</div>
